import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  ProdSeal,
  PaginatedProdSealResponse,
  CreateProdSealRequest,
} from "../../types/pages/prodseals";

interface ProdSealsState {
  prodseals: ProdSeal[];
  loading: boolean;
  error: string | null;

  createProdSealLoading: boolean;
  createProdSealError: string | null;
  createProdSealSuccess: boolean;

  selectedProdSeal: ProdSeal | null;
  fetchProdSealLoading: boolean;
  fetchProdSealError: string | null;

  totalCount: number;
  page: number;
  rowsPerPage: number;
  batchIdFilter: string | null;
  sortBy: string;
  sortOrder: number;

  previewUrl: string | null;

  downloadPdfLoading: boolean;
  downloadPdfError: string | null;

  previewPdfUrl: string | null;
  previewPdfLoading: boolean;
  previewPdfError: string | null;
}

const initialState: ProdSealsState = {
  prodseals: [],
  loading: false,
  error: null,

  createProdSealLoading: false,
  createProdSealError: null,
  createProdSealSuccess: false,

  selectedProdSeal: null,
  fetchProdSealLoading: false,
  fetchProdSealError: null,

  totalCount: 0,
  page: 0,
  rowsPerPage: 10,
  batchIdFilter: null,
  sortBy: "_id",
  sortOrder: -1,

  previewUrl: null,

  downloadPdfLoading: false,
  downloadPdfError: null,

  previewPdfUrl: null,
  previewPdfLoading: false,
  previewPdfError: null,
};

export interface PdfLayoutConfig {
  labelWidth: number;
  labelHeight: number;
  columns: number;
  margin: number;
  rowSpacing: number;
  columnSpacing: number;
  pageSize: string;
}

export interface ProdsealCustomizationPayload {
  // QR code rendering
  version: number;
  box_size: number;
  border: number;
  fill_color: string;
  back_color: string;
  batch_id?: string;

  // Label layout
  qr_size_mm: number;
  label_width_mm: number;
  label_height_mm: number;
  title: string;
  title_footer: string;
  title_font_size: number;
  footer_font_size: number;

  // Logo
  show_logo: boolean;
  logo_size_mm: number;

  // Border
  show_border: boolean;
  border_color: string;
  border_width_mm: number;
  border_radius_mm: number;
  label_padding_mm: number;

  // Output
  dpi: number;
}

const prodsealsSlice = createSlice({
  name: "prodseals",
  initialState,
  reducers: {
    fetchProdSeals: (
      state,
      action: PayloadAction<{
        page: number;
        rowsPerPage: number;
        batchId?: string;
        sortBy?: string;
        sortOrder?: number;
      }>,
    ) => {},
    fetchProdSealsStart: (state) => {
      state.loading = true;
      state.error = null;
    },
    fetchProdSealsSuccess: (
      state,
      action: PayloadAction<PaginatedProdSealResponse>,
    ) => {
      state.loading = false;
      state.prodseals = action.payload.data;
      state.totalCount = action.payload.total;
    },
    fetchProdSealsFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },

    createProdSeal: (state, action: PayloadAction<CreateProdSealRequest>) => {},
    createProdSealStart: (state) => {
      state.createProdSealLoading = true;
      state.createProdSealError = null;
      state.createProdSealSuccess = false;
    },
    createProdSealSuccess: (state, action: PayloadAction<ProdSeal>) => {
      state.createProdSealLoading = false;
      state.createProdSealSuccess = true;
      state.prodseals.unshift(action.payload);
    },
    createProdSealFailure: (state, action: PayloadAction<string>) => {
      state.createProdSealLoading = false;
      state.createProdSealError = action.payload;
      state.createProdSealSuccess = false;
    },

    setPage: (state, action: PayloadAction<number>) => {
      state.page = action.payload;
    },
    setRowsPerPage: (state, action: PayloadAction<number>) => {
      state.rowsPerPage = action.payload;
      state.page = 0;
    },
    setSortBy: (state, action: PayloadAction<string>) => {
      state.sortBy = action.payload;
      state.page = 0;
    },
    setSortOrder: (state, action: PayloadAction<number>) => {
      state.sortOrder = action.payload;
      state.page = 0;
    },

    setBatchIdFilter: (state, action: PayloadAction<string | null>) => {
      state.batchIdFilter = action.payload;
      state.page = 0;
    },

    clearProdSealsError: (state) => {
      state.error = null;
      state.createProdSealError = null;
      state.createProdSealSuccess = false;
    },

    clearProdSealsData: (state) => {
      state.prodseals = [];
      state.totalCount = 0;
      state.page = 0;
    },

    customizeProdseal: (
      state,
      action: PayloadAction<ProdsealCustomizationPayload>,
    ) => {},

    customizeProdsealStart(state) {
      state.loading = true;
      state.error = null;
    },
    customizeProdsealSuccess(state, action: PayloadAction<string>) {
      state.loading = false;
      state.previewUrl = action.payload;
    },
    customizeProdsealFailure(state, action: PayloadAction<string>) {
      state.loading = false;
      state.error = action.payload;
    },
    clearProdsealCustomizationError(state) {
      state.error = null;
    },
    clearProdsealPreview(state) {
      state.previewUrl = null;
    },

    fetchProdSealById: (state, action: PayloadAction<string>) => {},
    fetchProdSealByIdStart: (state) => {
      state.fetchProdSealLoading = true;
      state.fetchProdSealError = null;
    },
    fetchProdSealByIdSuccess: (state, action: PayloadAction<ProdSeal>) => {
      state.fetchProdSealLoading = false;
      state.selectedProdSeal = action.payload;
    },
    fetchProdSealByIdFailure: (state, action: PayloadAction<string>) => {
      state.fetchProdSealLoading = false;
      state.fetchProdSealError = action.payload;
    },
    clearSelectedProdSeal: (state) => {
      state.selectedProdSeal = null;
      state.fetchProdSealError = null;
    },

    downloadProdSealsPdf: (
      state,
      action: PayloadAction<{
        batchId: string;
        batchNumber: string;
        layout: PdfLayoutConfig;
      }>,
    ) => {},
    downloadProdSealsPdfStart: (state) => {
      state.downloadPdfLoading = true;
      state.downloadPdfError = null;
    },
    downloadProdSealsPdfSuccess: (state) => {
      state.downloadPdfLoading = false;
    },
    downloadProdSealsPdfFailure: (state, action: PayloadAction<string>) => {
      state.downloadPdfLoading = false;
      state.downloadPdfError = action.payload;
    },

    previewProdSealsPdf: (
      state,
      action: PayloadAction<{ batchId: string; layout: PdfLayoutConfig }>,
    ) => {},
    previewProdSealsPdfStart: (state) => {
      state.previewPdfLoading = true;
      state.previewPdfError = null;
    },
    previewProdSealsPdfSuccess: (state, action: PayloadAction<string>) => {
      state.previewPdfLoading = false;
      state.previewPdfUrl = action.payload;
    },
    previewProdSealsPdfFailure: (state, action: PayloadAction<string>) => {
      state.previewPdfLoading = false;
      state.previewPdfError = action.payload;
    },
    clearPreviewPdf: (state) => {
      if (state.previewPdfUrl) {
        URL.revokeObjectURL(state.previewPdfUrl);
      }
      state.previewPdfUrl = null;
      state.previewPdfError = null;
    },
  },
});

export const {
  fetchProdSeals,
  fetchProdSealsStart,
  fetchProdSealsSuccess,
  fetchProdSealsFailure,
  createProdSeal,
  createProdSealStart,
  createProdSealSuccess,
  createProdSealFailure,
  setPage,
  setRowsPerPage,
  setSortBy,
  setSortOrder,
  setBatchIdFilter,
  clearProdSealsError,
  clearProdSealsData,

  customizeProdseal,
  customizeProdsealStart,
  customizeProdsealSuccess,
  customizeProdsealFailure,
  clearProdsealCustomizationError,
  clearProdsealPreview,
  fetchProdSealById,
  fetchProdSealByIdStart,
  fetchProdSealByIdSuccess,
  fetchProdSealByIdFailure,
  clearSelectedProdSeal,
  downloadProdSealsPdf,
  downloadProdSealsPdfStart,
  downloadProdSealsPdfSuccess,
  downloadProdSealsPdfFailure,
  previewProdSealsPdf,
  previewProdSealsPdfStart,
  previewProdSealsPdfSuccess,
  previewProdSealsPdfFailure,
  clearPreviewPdf,
} = prodsealsSlice.actions;

export default prodsealsSlice.reducer;
